<template>
  <div
    v-click-outside="onClickOutside"
    class="service-search-machine"
  >
    <div class="header-wrapper">
      <label class="label">
        Service
      </label>
      <router-link
        aria-label="Create New Service (opens in a new window)"
        to="/service/0"
        class="create-new"
        target="_blank"
      >
        Create
      </router-link>
    </div>
    <div class="text-input-label">
      <input
        v-model="searchTerm"
        class="text-input"
        aria-label="Search"
        :class="{'flatten-bottom': showResults}"
        placeholder="Search"
        autocomplete="off"
        :aria-required="false"
        @input="onSearchTermChanged"
        @keyup.down="onDownArrowPressed"
        @keyup.up="onUpArrowPressed"
        @keyup.enter.stop="onEnterPressed"
        @keydown.tab="onClickOutside"
      >
      <div
        v-if="showResults"
        class="results-container"
      >
        <LoadingPulse
          :loading="searching"
          class="loading-spinner"
        />
        <div
          v-if="!searching"
          class="results"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="result-option"
            :class="result.highlighted ? 'highlighted' : ''"
            @click="chooseOption(result)"
          >
            <div class="result-option-name">
              {{ result.name }}
            </div>
          </div>
          <div
            v-if="searchResults.length == 0"
            class="no-results-container"
          >
            <div class="no-results">
              No results
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { ServiceService } from '@/services/service.service'
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { Service } from '@/@types/responses/service/Service'
import Toaster from '@meforma/vue-toaster'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'ServiceSearchMachine',
  components: {
    LoadingPulse
  },
  emits: [
    'service'
  ],
  setup() {
    const serviceService = inject(PROVIDER_CONSTANTS.serviceService) as ServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function createDebounce() {
      let timeout: number|undefined = undefined
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return function (func: any, delay?: number|undefined) {
        clearTimeout(timeout)
        timeout = setTimeout(() => { func() }, delay || 500)
      }
    }
    return {
      debounce: createDebounce(),
      serviceService,
      toast
    }
  },
  data() {
    return {
      searching: false,
      searchTerm: '',
      searchResults: [] as Service[]
    }
  },
  computed: {
    showResults(): boolean {
      return this.searchTerm !== '';
    }
  },
  methods: {
    chooseOption(service: Service): void {
      this.searchTerm = ''
      this.$emit('service', service)
    },
    getHighlightedService(): Service|undefined {
      const highlightedService = this.searchResults.filter(s => s.highlighted);
      return highlightedService.length > 0 ? highlightedService[0] : undefined;
    },
    onClickOutside(): void {
      this.searchTerm = ''
    },
    onDownArrowPressed(): void {
      const highlightedService = this.getHighlightedService();

      let nextIndex = highlightedService ? this.searchResults.indexOf(highlightedService) + 1 : 0;
      if (nextIndex > this.searchResults.length - 1)
        nextIndex = 0;
      
      if (highlightedService)
        highlightedService.highlighted = false;

      this.searchResults[nextIndex].highlighted = true;
    },
    onEnterPressed(): void {
      const highlightedService = this.getHighlightedService();
      if (highlightedService)
        this.chooseOption(highlightedService)
    },
    onSearchTermChanged(): void {
      if (this.searchTerm !== '') {
        this.searching = true
        this.debounce(this.searchServices)
      }
    },
    onUpArrowPressed(): void {
      const highlightedService = this.getHighlightedService();
      let previousIndex = highlightedService ? this.searchResults.indexOf(highlightedService) - 1 : 0;

      if (previousIndex < 0)
        previousIndex = this.searchResults.length - 1;

      if (highlightedService)
        highlightedService.highlighted = false;

      this.searchResults[previousIndex].highlighted = true;
    },
    searchServices(): void {
      this.serviceService.searchServices(this.searchTerm, 1, 25, 'Name', undefined, true).then(pagingResponse => {
        this.searchResults = pagingResponse.results
        this.searching = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error(`Failed to search for your services. Please try again.`)
        }
      })
    }
  }
})
</script>

<style scoped lang="scss">
.service-search-machine {
  @include sac-search-machine;
}
</style>